import {
  AddIcon,
  Button,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  GroupsIcon,
  VisibleIcon,
} from "@bp/ui-components";
import { useState } from "react";
import { useMutation, useQuery } from "urql";
import { type FragmentType, getFragmentData, graphql } from "../../../client/gql";
import { useConfirm } from "../../../hooks/useConfirm";
import { showSuccessToast } from "../../../utils/toast";
import { MemberList, MemberRow } from "../OmniOrgView/MemberRow";
import { GroupMembersModal } from "../modals/GroupMembersModal";
import { GroupModal } from "../modals/GroupModal";
import css from "../orgList.module.scss";

// Colocated fragments: the org node this panel needs plus the Group shape.
const GroupsPanel_Organization = graphql(`
  fragment GroupsPanel_Organization on Organization {
    uuid
    name
  }
`);

const GroupsPanel_Group = graphql(`
  fragment GroupsPanel_Group on Group {
    uuid
    name
    publicVisible
    profilesConnection {
      totalCount
    }
    profiles {
      uuid
    }
  }
`);

const GroupsPanelQuery = graphql(`
  query GroupsPanel_Query($where: GroupWhere) {
    groups(where: $where) {
      ...GroupsPanel_Group
    }
  }
`);

const GroupsPanelDeleteGroup = graphql(`
  mutation GroupsPanel_DeleteGroup($where: GroupWhere) {
    deleteGroups(where: $where) {
      __typename
    }
  }
`);

export function GroupsPanel({
  org: orgRef,
}: {
  org: FragmentType<typeof GroupsPanel_Organization>;
}) {
  const org = getFragmentData(GroupsPanel_Organization, orgRef);
  const [{ data }] = useQuery({
    query: GroupsPanelQuery,
    variables: { where: { organization: { uuid: org.uuid } } },
  });
  const [, deleteGroup] = useMutation(GroupsPanelDeleteGroup);
  const { confirm, ConfirmationDialog } = useConfirm();
  const [adding, setAdding] = useState(false);

  const groups = getFragmentData(GroupsPanel_Group, data?.groups ?? []);
  const [editing, setEditing] = useState<(typeof groups)[number] | null>(null);
  const [membersTarget, setMembersTarget] = useState<(typeof groups)[number] | null>(null);

  const onDelete = async (uuid: string, name: string) => {
    const ok = await confirm({
      title: "Gruppe löschen",
      message: `„${name}" löschen?`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    await deleteGroup({ where: { uuid } });
    showSuccessToast(`Gruppe „${name}" gelöscht`);
  };

  return (
    <div className="card" style={{ background: "var(--color-white)" }}>
      <div className={css.listToolbar}>
        <div className={css.listToolbarRow}>
          <div className={css.listToolbarLeft}>
            <span className={css.eyebrow}>Gruppen in {org.name}</span>
            <span className={css.countMuted}>{groups.length} Gruppen</span>
          </div>
          <Button
            hierarchy="secondary"
            height="s"
            icon={<AddIcon />}
            onClick={() => setAdding(true)}
          >
            Gruppe anlegen
          </Button>
        </div>
      </div>

      <MemberList
        isEmpty={groups.length === 0}
        emptyTitle="Keine Gruppen"
        emptySub="Lege eine Gruppe an, um Mitglieder dieser Organisation zu bündeln."
      >
        {groups.map((g) => {
          const count = g.profilesConnection?.totalCount ?? 0;
          return (
            <MemberRow
              key={g.uuid}
              onClick={() => setMembersTarget(g)}
              status={<GroupsIcon style={{ fontSize: 18, color: "var(--color-grey-dark)" }} />}
              statusTint="var(--color-grey-lightest)"
              name={
                <>
                  {g.name}
                  {g.publicVisible && (
                    <Chip
                      bgColor="var(--color-grey-lightest)"
                      color="var(--color-grey-dark)"
                      isSmall
                      value={
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <VisibleIcon style={{ fontSize: 10 }} />
                          Öffentlich
                        </span>
                      }
                    />
                  )}
                </>
              }
              sub={`${count} ${count === 1 ? "Mitglied" : "Mitglieder"}`}
              actions={
                <Dropdown
                  trigger={
                    <Button
                      hierarchy="tertiary"
                      icon={<DotsHorizontalIcon />}
                      ariaLabel="Aktionen"
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                >
                  <DropdownMenu
                    data={[
                      { label: "Mitglieder bearbeiten", onClick: () => setMembersTarget(g) },
                      { label: "Umbenennen", onClick: () => setEditing(g) },
                      { type: "ruler" },
                      {
                        label: "Löschen",
                        color: "error",
                        onClick: () => void onDelete(g.uuid, g.name),
                      },
                    ]}
                  />
                </Dropdown>
              }
            />
          );
        })}
      </MemberList>

      {adding && (
        <GroupModal
          org={{ uuid: org.uuid, name: org.name }}
          onClose={() => setAdding(false)}
          onSaved={() => setAdding(false)}
        />
      )}
      {editing && (
        <GroupModal
          org={{ uuid: org.uuid, name: org.name }}
          group={{ uuid: editing.uuid, name: editing.name, publicVisible: editing.publicVisible }}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}
      {membersTarget && (
        <GroupMembersModal
          org={{ uuid: org.uuid, name: org.name }}
          group={{
            uuid: membersTarget.uuid,
            name: membersTarget.name,
            memberUuids: (membersTarget.profiles ?? []).map((p) => p.uuid),
          }}
          onClose={() => setMembersTarget(null)}
          onSaved={() => setMembersTarget(null)}
        />
      )}
      <ConfirmationDialog />
    </div>
  );
}
